class KlaviyoNewsletterSignup extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['form', 'submit', 'formError', 'success']
  }

  static get requiredAttributes () {
    return ['data-list-id']
  }

  get listId () {
    return this.dataset.listId
  }

  get consentText () {
    return this.dataset.consentText || ''
  }

  get phoneRequired () {
    return this.els.phone?.element.getAttribute('aria-required') === 'true'
  }

  get companyId () {
    return document.querySelector('script[src*="klaviyo.js"]')?.src.match(/company_id=([^&]+)/)?.[1] ?? ''
  }

  get _dropdownIsOpen () {
    return this.els.dropdown?.exists && !this.els.dropdown.element.hidden
  }

  // Lifecycle //

  setupListeners () {
    super.setupListeners()

    this.listeners.add(this.els.form.element, 'submit', this._onSubmit.bind(this))

    if (this.els.email?.exists) {
      this.listeners.add(this.els.email.element, 'input', this._clearFieldError.bind(this, 'email'))
    }

    if (this.els.phone?.exists) {
      this.listeners.add(this.els.phone.element, 'input', this._clearFieldError.bind(this, 'phone'))
    }

    if (this.els.countryBtn?.exists) {
      this._dropdownRenderList(COUNTRIES)
      this.listeners.add(this.els.countryBtn.element, 'click', this._onCountryBtnClick.bind(this))
      this.listeners.add(this.els.searchInput.element, 'input', this._onCountrySearch.bind(this))
      this.listeners.add(this.els.countryList.element, 'click', this._onCountryListClick.bind(this))
      this.listeners.add(this.els.searchInput.element, 'keydown', this._onCountrySearchKeydown.bind(this))
      this.listeners.add(this.els.countryList.element, 'keydown', this._onCountryListKeydown.bind(this))
      this.listeners.add(document, 'click', this._onDocumentClick.bind(this))
    }
  }

  // Form //

  _validate () {
    let valid = true

    if (!this.els.email.element.value.trim()) {
      this._showFieldError('email', 'Please enter your email address')
      valid = false
    }

    if (this.phoneRequired && this.els.phone?.exists && !this.els.phone.element.value.trim()) {
      this._showFieldError('phone', 'Please enter your phone number')
      valid = false
    }

    if (this.els.consent?.exists && !this.els.consent.element.checked) {
      this._showFormError('Please tick the box to agree to our terms before signing up')
      valid = false
    }

    return valid
  }

  _showFieldError (name, message) {
    this.els[name]?.element.classList.add('is-invalid')
    const errorEl = this.els[`${name}Error`]
    if (errorEl?.exists) {
      errorEl.element.textContent = message
      errorEl.element.classList.add('is-visible')
    }
  }

  _clearFieldError (name) {
    this.els[name]?.element.classList.remove('is-invalid')
    const errorEl = this.els[`${name}Error`]
    if (errorEl?.exists) {
      errorEl.element.textContent = ''
      errorEl.element.classList.remove('is-visible')
    }
    this._showFormError('')
  }

  _showFormError (message) {
    this.els.formError.element.textContent = message
  }

  _setLoading (isLoading) {
    this.els.submit.element.disabled = isLoading
    this.els.submit.element.classList.toggle('is-loading', isLoading)
  }

  async _onSubmit (event) {
    event.preventDefault()

    this._showFormError('')
    this._clearFieldError('email')

    if (!this.listId) {
      this._showFormError('Form is not configured correctly. Please contact the store owner.')
      console.error('[klaviyo-newsletter-signup] data-list-id attribute is empty. Set the Klaviyo List ID in the section settings.')
      return
    }

    if (!this._validate()) return

    this._setLoading(true)

    try {
      const profileAttributes = {
        email: this.els.email.element.value.trim(),
        subscriptions: {
          email: { marketing: { consent: 'SUBSCRIBED' } },
        },
        properties: {
          $consent: [this.consentText],
          $consent_method: 'Klaviyo Form',
        },
      }

      if (this.els.firstName?.exists) profileAttributes.first_name = this.els.firstName.element.value.trim()
      if (this.els.lastName?.exists) profileAttributes.last_name = this.els.lastName.element.value.trim()

      const rawPhone = this.els.phone?.element.value.trim()
      if (rawPhone) {
        const dialCode = this.els.countryBtn?.element.dataset.dial || '+1'
        const digitsOnly = (str) => str.replace(/\D/g, '')
        profileAttributes.phone_number = `+${digitsOnly(dialCode)}${digitsOnly(rawPhone)}`
        profileAttributes.subscriptions.sms = { marketing: { consent: 'SUBSCRIBED' } }
      }

      const relationships = {
        list: { data: { type: 'list', id: this.listId } },
      }

      const response = await fetch(
        `https://a.klaviyo.com/client/subscriptions/?company_id=${this.companyId}`,
        {
          method: 'POST',
          headers: {
            revision: '2026-01-15',
            accept: 'application/vnd.api+json',
            'content-type': 'application/vnd.api+json',
          },
          body: JSON.stringify({
            data: {
              type: 'subscription',
              attributes: {
                profile: { data: { type: 'profile', attributes: profileAttributes } },
                custom_source: 'Newsletter Signup Page',
              },
              relationships,
            },
          }),
        },
      )

      if (response.ok || response.status === 202 || response.status === 409) {
        this._onSuccess()
      } else {
        const body = await response.json().catch(() => null)
        this._showFormError(body?.errors?.[0]?.detail || 'Something went wrong. Please try again.')
      }
    } catch (error) {
      console.error('[klaviyo-newsletter-signup] Submission error:', error)
      this._showFormError('Something went wrong. Please check your connection and try again.')
    } finally {
      this._setLoading(false)
    }
  }

  _onSuccess () {
    this.els.form.element.hidden = true
    this.els.success.element.hidden = false
    this.els.success.element.focus()
  }

  // Country dropdown //

  _dropdownRenderList (countries) {
    this.els.countryList.element.innerHTML = countries
      .map(
        ([name, iso, dial]) =>
          `<li
            class="sec-NewsletterSignup_CountryOption"
            role="option"
            tabindex="-1"
            data-dial="${dial}"
            data-iso="${iso}"
            data-name="${name}">
            <span class="sec-NewsletterSignup_OptionFlag" aria-hidden="true">${_isoToFlag(iso)}</span>
            <span class="sec-NewsletterSignup_OptionName">${name}</span>
            <span class="sec-NewsletterSignup_OptionDial">${dial}</span>
          </li>`,
      )
      .join('')
  }

  _dropdownOpen () {
    this.els.dropdown.element.hidden = false
    this.els.countryBtn.element.setAttribute('aria-expanded', 'true')
    this.els.searchInput.element.value = ''
    this._dropdownRenderList(COUNTRIES)
    requestAnimationFrame(() => this.els.searchInput.element.focus())
  }

  _dropdownClose () {
    this.els.dropdown.element.hidden = true
    this.els.countryBtn.element.setAttribute('aria-expanded', 'false')
  }

  _dropdownSelect (name, iso, dial) {
    this.els.countryBtn.element.dataset.dial = dial
    this.els.countryBtn.element.setAttribute('aria-label', `${name} (${dial})`)
    this.els.countryFlag.element.textContent = _isoToFlag(iso)
    this._dropdownClose()
    this.els.countryBtn.element.focus()
  }

  _onCountryBtnClick (event) {
    event.stopPropagation()
    this._dropdownIsOpen ? this._dropdownClose() : this._dropdownOpen()
  }

  _onCountrySearch () {
    const searchQuery = this.els.searchInput.element.value.toLowerCase()
    this._dropdownRenderList(
      COUNTRIES.filter(([name, _iso, dial]) => name.toLowerCase().includes(searchQuery) || dial.includes(searchQuery)),
    )
  }

  _onCountryListClick (event) {
    const option = event.target.closest('.sec-NewsletterSignup_CountryOption')
    if (!option) return
    this._dropdownSelect(option.dataset.name, option.dataset.iso, option.dataset.dial)
  }

  _onCountrySearchKeydown (event) {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      this.els.countryList.element.querySelector('.sec-NewsletterSignup_CountryOption')?.focus()
    }
    if (event.key === 'Escape') {
      this._dropdownClose()
      this.els.countryBtn.element.focus()
    }
  }

  _onCountryListKeydown (event) {
    const option = event.target.closest('.sec-NewsletterSignup_CountryOption')
    if (!option) return

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      this._dropdownSelect(option.dataset.name, option.dataset.iso, option.dataset.dial)
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      (option.nextElementSibling || this.els.countryList.element.firstElementChild)?.focus()
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      option.previousElementSibling
        ? option.previousElementSibling.focus()
        : this.els.searchInput.element.focus()
    } else if (event.key === 'Escape') {
      this._dropdownClose()
      this.els.countryBtn.element.focus()
    }
  }

  _onDocumentClick (event) {
    if (this._dropdownIsOpen && !this.contains(event.target)) {
      this._dropdownClose()
    }
  }
}

function _isoToFlag (iso) {
  const REGIONAL_INDICATOR_A = 0x1f1e6
  const CHAR_CODE_A = 'A'.charCodeAt(0)
  return [...iso.toUpperCase()]
    .map((letter) => String.fromCodePoint(REGIONAL_INDICATOR_A + letter.charCodeAt(0) - CHAR_CODE_A))
    .join('')
}

const COUNTRIES = [
  ['Afghanistan', 'AF', '+93'],
  ['Albania', 'AL', '+355'],
  ['Algeria', 'DZ', '+213'],
  ['American Samoa', 'AS', '+1'],
  ['Andorra', 'AD', '+376'],
  ['Angola', 'AO', '+244'],
  ['Anguilla', 'AI', '+1'],
  ['Antigua and Barbuda', 'AG', '+1'],
  ['Argentina', 'AR', '+54'],
  ['Armenia', 'AM', '+374'],
  ['Aruba', 'AW', '+297'],
  ['Australia', 'AU', '+61'],
  ['Austria', 'AT', '+43'],
  ['Azerbaijan', 'AZ', '+994'],
  ['Bahamas', 'BS', '+1'],
  ['Bahrain', 'BH', '+973'],
  ['Bangladesh', 'BD', '+880'],
  ['Barbados', 'BB', '+1'],
  ['Belarus', 'BY', '+375'],
  ['Belgium', 'BE', '+32'],
  ['Belize', 'BZ', '+501'],
  ['Benin', 'BJ', '+229'],
  ['Bermuda', 'BM', '+1'],
  ['Bhutan', 'BT', '+975'],
  ['Bolivia', 'BO', '+591'],
  ['Bosnia and Herzegovina', 'BA', '+387'],
  ['Botswana', 'BW', '+267'],
  ['Brazil', 'BR', '+55'],
  ['British Virgin Islands', 'VG', '+1'],
  ['Brunei', 'BN', '+673'],
  ['Bulgaria', 'BG', '+359'],
  ['Burkina Faso', 'BF', '+226'],
  ['Burundi', 'BI', '+257'],
  ['Cambodia', 'KH', '+855'],
  ['Cameroon', 'CM', '+237'],
  ['Canada', 'CA', '+1'],
  ['Cape Verde', 'CV', '+238'],
  ['Cayman Islands', 'KY', '+1'],
  ['Central African Republic', 'CF', '+236'],
  ['Chad', 'TD', '+235'],
  ['Chile', 'CL', '+56'],
  ['China', 'CN', '+86'],
  ['Colombia', 'CO', '+57'],
  ['Comoros', 'KM', '+269'],
  ['Cook Islands', 'CK', '+682'],
  ['Costa Rica', 'CR', '+506'],
  ['Croatia', 'HR', '+385'],
  ['Cuba', 'CU', '+53'],
  ['Curaçao', 'CW', '+599'],
  ['Cyprus', 'CY', '+357'],
  ['Czech Republic', 'CZ', '+420'],
  ['Dem. Rep. of the Congo', 'CD', '+243'],
  ['Denmark', 'DK', '+45'],
  ['Djibouti', 'DJ', '+253'],
  ['Dominica', 'DM', '+1'],
  ['Dominican Republic', 'DO', '+1'],
  ['Ecuador', 'EC', '+593'],
  ['Egypt', 'EG', '+20'],
  ['El Salvador', 'SV', '+503'],
  ['Equatorial Guinea', 'GQ', '+240'],
  ['Eritrea', 'ER', '+291'],
  ['Estonia', 'EE', '+372'],
  ['Eswatini', 'SZ', '+268'],
  ['Ethiopia', 'ET', '+251'],
  ['Falkland Islands', 'FK', '+500'],
  ['Faroe Islands', 'FO', '+298'],
  ['Fiji', 'FJ', '+679'],
  ['Finland', 'FI', '+358'],
  ['France', 'FR', '+33'],
  ['French Guiana', 'GF', '+594'],
  ['French Polynesia', 'PF', '+689'],
  ['Gabon', 'GA', '+241'],
  ['Gambia', 'GM', '+220'],
  ['Georgia', 'GE', '+995'],
  ['Germany', 'DE', '+49'],
  ['Ghana', 'GH', '+233'],
  ['Gibraltar', 'GI', '+350'],
  ['Greece', 'GR', '+30'],
  ['Greenland', 'GL', '+299'],
  ['Grenada', 'GD', '+1'],
  ['Guadeloupe', 'GP', '+590'],
  ['Guam', 'GU', '+1'],
  ['Guatemala', 'GT', '+502'],
  ['Guinea', 'GN', '+224'],
  ['Guinea-Bissau', 'GW', '+245'],
  ['Guyana', 'GY', '+592'],
  ['Haiti', 'HT', '+509'],
  ['Honduras', 'HN', '+504'],
  ['Hong Kong', 'HK', '+852'],
  ['Hungary', 'HU', '+36'],
  ['Iceland', 'IS', '+354'],
  ['India', 'IN', '+91'],
  ['Indonesia', 'ID', '+62'],
  ['Iran', 'IR', '+98'],
  ['Iraq', 'IQ', '+964'],
  ['Ireland', 'IE', '+353'],
  ['Isle of Man', 'IM', '+44'],
  ['Israel', 'IL', '+972'],
  ['Italy', 'IT', '+39'],
  ['Ivory Coast', 'CI', '+225'],
  ['Jamaica', 'JM', '+1'],
  ['Japan', 'JP', '+81'],
  ['Jordan', 'JO', '+962'],
  ['Kazakhstan', 'KZ', '+7'],
  ['Kenya', 'KE', '+254'],
  ['Kiribati', 'KI', '+686'],
  ['Kosovo', 'XK', '+383'],
  ['Kuwait', 'KW', '+965'],
  ['Kyrgyzstan', 'KG', '+996'],
  ['Laos', 'LA', '+856'],
  ['Latvia', 'LV', '+371'],
  ['Lebanon', 'LB', '+961'],
  ['Lesotho', 'LS', '+266'],
  ['Liberia', 'LR', '+231'],
  ['Libya', 'LY', '+218'],
  ['Liechtenstein', 'LI', '+423'],
  ['Lithuania', 'LT', '+370'],
  ['Luxembourg', 'LU', '+352'],
  ['Macao', 'MO', '+853'],
  ['Madagascar', 'MG', '+261'],
  ['Malawi', 'MW', '+265'],
  ['Malaysia', 'MY', '+60'],
  ['Maldives', 'MV', '+960'],
  ['Mali', 'ML', '+223'],
  ['Malta', 'MT', '+356'],
  ['Marshall Islands', 'MH', '+692'],
  ['Martinique', 'MQ', '+596'],
  ['Mauritania', 'MR', '+222'],
  ['Mauritius', 'MU', '+230'],
  ['Mayotte', 'YT', '+262'],
  ['Mexico', 'MX', '+52'],
  ['Micronesia', 'FM', '+691'],
  ['Moldova', 'MD', '+373'],
  ['Monaco', 'MC', '+377'],
  ['Mongolia', 'MN', '+976'],
  ['Montenegro', 'ME', '+382'],
  ['Montserrat', 'MS', '+1'],
  ['Morocco', 'MA', '+212'],
  ['Mozambique', 'MZ', '+258'],
  ['Myanmar', 'MM', '+95'],
  ['Namibia', 'NA', '+264'],
  ['Nauru', 'NR', '+674'],
  ['Nepal', 'NP', '+977'],
  ['Netherlands', 'NL', '+31'],
  ['New Caledonia', 'NC', '+687'],
  ['New Zealand', 'NZ', '+64'],
  ['Nicaragua', 'NI', '+505'],
  ['Niger', 'NE', '+227'],
  ['Nigeria', 'NG', '+234'],
  ['Niue', 'NU', '+683'],
  ['North Korea', 'KP', '+850'],
  ['North Macedonia', 'MK', '+389'],
  ['Northern Mariana Islands', 'MP', '+1'],
  ['Norway', 'NO', '+47'],
  ['Oman', 'OM', '+968'],
  ['Pakistan', 'PK', '+92'],
  ['Palau', 'PW', '+680'],
  ['Palestine', 'PS', '+970'],
  ['Panama', 'PA', '+507'],
  ['Papua New Guinea', 'PG', '+675'],
  ['Paraguay', 'PY', '+595'],
  ['Peru', 'PE', '+51'],
  ['Philippines', 'PH', '+63'],
  ['Poland', 'PL', '+48'],
  ['Portugal', 'PT', '+351'],
  ['Puerto Rico', 'PR', '+1'],
  ['Qatar', 'QA', '+974'],
  ['Republic of the Congo', 'CG', '+242'],
  ['Reunion', 'RE', '+262'],
  ['Romania', 'RO', '+40'],
  ['Russia', 'RU', '+7'],
  ['Rwanda', 'RW', '+250'],
  ['Saint Kitts and Nevis', 'KN', '+1'],
  ['Saint Lucia', 'LC', '+1'],
  ['Saint Vincent and the Grenadines', 'VC', '+1'],
  ['Samoa', 'WS', '+685'],
  ['San Marino', 'SM', '+378'],
  ['Saudi Arabia', 'SA', '+966'],
  ['Senegal', 'SN', '+221'],
  ['Serbia', 'RS', '+381'],
  ['Seychelles', 'SC', '+248'],
  ['Sierra Leone', 'SL', '+232'],
  ['Singapore', 'SG', '+65'],
  ['Sint Maarten', 'SX', '+1'],
  ['Slovakia', 'SK', '+421'],
  ['Slovenia', 'SI', '+386'],
  ['Solomon Islands', 'SB', '+677'],
  ['Somalia', 'SO', '+252'],
  ['South Africa', 'ZA', '+27'],
  ['South Korea', 'KR', '+82'],
  ['South Sudan', 'SS', '+211'],
  ['Spain', 'ES', '+34'],
  ['Sri Lanka', 'LK', '+94'],
  ['Sudan', 'SD', '+249'],
  ['Suriname', 'SR', '+597'],
  ['Sweden', 'SE', '+46'],
  ['Switzerland', 'CH', '+41'],
  ['Syria', 'SY', '+963'],
  ['Taiwan', 'TW', '+886'],
  ['Tajikistan', 'TJ', '+992'],
  ['Tanzania', 'TZ', '+255'],
  ['Thailand', 'TH', '+66'],
  ['Togo', 'TG', '+228'],
  ['Tonga', 'TO', '+676'],
  ['Trinidad and Tobago', 'TT', '+1'],
  ['Tunisia', 'TN', '+216'],
  ['Turkey', 'TR', '+90'],
  ['Turkmenistan', 'TM', '+993'],
  ['Turks and Caicos Islands', 'TC', '+1'],
  ['Tuvalu', 'TV', '+688'],
  ['US Virgin Islands', 'VI', '+1'],
  ['Uganda', 'UG', '+256'],
  ['Ukraine', 'UA', '+380'],
  ['United Arab Emirates', 'AE', '+971'],
  ['United Kingdom', 'GB', '+44'],
  ['United States', 'US', '+1'],
  ['Uruguay', 'UY', '+598'],
  ['Uzbekistan', 'UZ', '+998'],
  ['Vanuatu', 'VU', '+678'],
  ['Venezuela', 'VE', '+58'],
  ['Vietnam', 'VN', '+84'],
  ['Wallis and Futuna', 'WF', '+681'],
  ['Yemen', 'YE', '+967'],
  ['Zambia', 'ZM', '+260'],
  ['Zimbabwe', 'ZW', '+263'],
  ['Åland Islands', 'AX', '+358'],
].sort((a, b) => a[0].localeCompare(b[0]))

if (!customElements.get('klaviyo-newsletter-signup')) {
  customElements.define('klaviyo-newsletter-signup', KlaviyoNewsletterSignup)
}
